import { css } from '@emotion/react';
import DownloadIcon from '@mui/icons-material/Download';
import { IconButton, Stack } from '@mui/material';
import { useAtomValue } from 'jotai';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback, useEffect, useState } from 'react';
import { GenericFallbacksWrapper } from '../../../../components/Fallback/GenericFallbacksWrapper';
import { companyStateJ } from '../../../../services/state/CompanyStateJ';
import { nonEditableCellCss, nonEditableRowCss } from '../../../../theme/gridStyle';
import { MaggieFeatureFlags } from '../../../../util/feature-flags';
import { useInitFinancialsDateSettings } from '../../../CompanyProfiles2/hooks/useInitFinancialsDateSettings';
import { allCompanyFinancialsState } from '../../state/CompanyFinancialsState';
import { selectedCompanyIdProfileAtom } from '../../state/UIStateJ';
import { FinancialsDateSettings2 } from '../FinancialsDatePickers/FinancialsDateSettings2';
import {
  selectedEndDateFinancialsState,
  selectedFinancialsRangeState,
  selectedFrequencyFinancialsState,
} from '../FinancialsDatePickers/useFinancialsDateSettings';
import { CompanyProfileFinancialsExportModal } from './components/CompanyProfileFinancialsExportModal';
import { CompanyProfileFinancialsTable } from './components/CompanyProfileFinancialsTable';
import { FinancialsGrid2 } from './components/FinancialsGrid2';
import { FinancialsSettings } from './components/FinancialsSettings';
import { FyeChip } from './components/FyeChip';
import { FinancialsCurrencyToggle } from './components/FinancialsCurrencyToggle';
import { FinancialsTypeToggle, KpiType, useFinancialsType } from './FinancialsTypeToggle';
import { TextualKpis } from './textualKpis/TextualKpis';
import { trackEvent } from '../../../../util/datadogTracking';

export const ContentCss = css`
  gap: 0.5rem;
  padding: 1rem 2.5rem;
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

export function CompanyProfileFinancials() {
  const initialize = useInitFinancialsDateSettings();
  const { selectedKpiType } = useFinancialsType();
  const initialized = initialize();

  if (!initialized) {
    return null;
  }
  return <CompanyProfileFinancialsContent selectedKpiType={selectedKpiType} />;
}

export function CompanyProfileFinancialsContent({ selectedKpiType }: { selectedKpiType: KpiType }) {
  const companyId = useAtomValue(selectedCompanyIdProfileAtom);
  const company = useAtomValue(companyStateJ(companyId));
  const fye = company?.fye ?? 12;

  const reportingFrequency = useAtomValue(selectedFrequencyFinancialsState)!;
  const selectedEndDate = useAtomValue(selectedEndDateFinancialsState);

  const [openModal, setOpenModal] = useState(false);

  // Statement view opened (FD-3121). Fires once per mount of the financials
  // content; `kpi_type` distinguishes the quantitative grid from textual KPIs.
  useEffect(() => {
    trackEvent('finance.statement_viewed', { kpi_type: selectedKpiType });
  }, [selectedKpiType]);

  const handleCloseModal = useCallback(() => {
    setOpenModal(false);
  }, []);

  const handleOpenModal = useCallback(() => {
    setOpenModal(true);
  }, []);

  const { showFinancialsGrid2 } = useFlags<MaggieFeatureFlags>();
  const selectedPeriodRange = useAtomValue(selectedFinancialsRangeState);
  const financials = useAtomValue(allCompanyFinancialsState(companyId));

  if (!selectedPeriodRange || selectedPeriodRange <= 0) {
    return null;
  }

  return (
    <GenericFallbacksWrapper>
      <Stack css={ContentCss}>
        {openModal && <CompanyProfileFinancialsExportModal open={openModal} onClose={handleCloseModal} />}
        <Stack direction='row' justifyContent='space-between' alignItems='center' width='100%'>
          <Stack direction='row' alignItems='center' gap='1rem'>
            <FinancialsTypeToggle />
            <Stack direction={'row'} alignItems={'center'} gap={'1rem'}>
              <FinancialsDateSettings2 />
            </Stack>
          </Stack>
          <Stack direction='row' alignItems={'center'} gap='0.2rem' justifyContent={'center'} ml='0.5rem'>
            <FyeChip fye={fye} />
            <FinancialsCurrencyToggle />
            <IconButton onClick={handleOpenModal} aria-label='export' color='secondary'>
              <DownloadIcon />
            </IconButton>
            {selectedKpiType == KpiType.Quantitative && <FinancialsSettings />}
          </Stack>
        </Stack>
        <Stack
          height='100%'
          overflow='hidden'
          css={css`
            ${nonEditableCellCss};
            ${nonEditableRowCss};
          `}
        >
          {selectedKpiType == KpiType.Qualitative ? (
            <TextualKpis />
          ) : showFinancialsGrid2 ? (
            <FinancialsGrid2
              nSelectedPeriods={selectedPeriodRange!}
              period={reportingFrequency}
              financials={financials}
              fye={fye}
              endDate={selectedEndDate}
            />
          ) : (
            <CompanyProfileFinancialsTable
              totalSelectedPeriods={selectedPeriodRange!}
              frequency={reportingFrequency}
              fye={fye}
            />
          )}
        </Stack>
      </Stack>
    </GenericFallbacksWrapper>
  );
}
